import { Mark, mergeAttributes } from '@tiptap/core';

export interface CommentHighlightOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    commentHighlight: {
      setCommentHighlight: (attributes: { commentId: string; number: number; color: string; textChanged?: boolean }) => ReturnType;
      unsetCommentHighlight: () => ReturnType;
    };
  }
}

export const CommentHighlight = Mark.create<CommentHighlightOptions>({
  name: 'commentHighlight',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: element => element.getAttribute('data-comment-id'),
        renderHTML: attributes => {
          if (!attributes.commentId) {
            return {};
          }
          return {
            'data-comment-id': attributes.commentId,
          };
        },
      },
      number: {
        default: 1,
        parseHTML: element => parseInt(element.getAttribute('data-comment-number') || '1'),
        renderHTML: attributes => {
          return {
            'data-comment-number': attributes.number,
          };
        },
      },
      color: {
        default: 'hsl(var(--primary))',
        parseHTML: element => element.getAttribute('data-comment-color'),
        renderHTML: attributes => {
          return {
            'data-comment-color': attributes.color,
          };
        },
      },
      textChanged: {
        default: false,
        parseHTML: element => element.getAttribute('data-text-changed') === 'true',
        renderHTML: attributes => {
          return {
            'data-text-changed': attributes.textChanged,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Safely extract attributes with defaults
    const commentColor = HTMLAttributes?.['data-comment-color'] || 'blue';
    const commentNumber = String(HTMLAttributes?.['data-comment-number'] || '1');
    
    // Map color names to actual color values
    const colorMap: Record<string, string> = {
      blue: '#3b82f6',
      amber: '#f59e0b',
      orange: '#f97316',
      purple: '#a855f7',
      green: '#22c55e',
      pink: '#ec4899',
      teal: '#14b8a6',
    };
    
    const colorValue = colorMap[commentColor] || commentColor;
    
    // Simplified structure without nested badge to avoid renderSpec issues
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes || {}, {
        class: 'comment-highlight relative inline',
        style: `
          text-decoration: underline;
          text-decoration-color: ${colorValue};
          text-decoration-thickness: 2px;
          text-underline-offset: 2px;
          cursor: pointer;
          position: relative;
          --comment-badge-color: ${colorValue};
        `,
        'data-badge-number': commentNumber,
      }),
      0, // Content slot - this is where the marked text goes
    ];
  },

  addCommands() {
    return {
      setCommentHighlight:
        (attributes) =>
        ({ commands }) => {
          return commands.setMark(this.name, attributes);
        },
      unsetCommentHighlight:
        () =>
        ({ commands }) => {
          return commands.unsetMark(this.name);
        },
    };
  },
});
